import type { Note, Score } from "./score.js";

const noiseBuffers = new WeakMap<BaseAudioContext, AudioBuffer>();
function noise(context: BaseAudioContext): AudioBuffer {
  const cached = noiseBuffers.get(context);
  if (cached) return cached;
  const buffer = context.createBuffer(1, context.sampleRate * 2, context.sampleRate);
  const data = buffer.getChannelData(0);
  let seed = 0x5a454e44;
  for (let i = 0; i < data.length; i++) {
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
    data[i] = seed / 0x80000000 - 1;
  }
  noiseBuffers.set(context, buffer);
  return buffer;
}

export interface Voice { gain: number; stop(): void; disconnect(): void }
export interface Mix { sound: GainNode; music: GainNode; dispose(): void }

/** Shared live/offline signal path; gentle dynamics and conservative output headroom. */
export function createMix(context: BaseAudioContext, sound = 1, music = 1): Mix {
  const fx = context.createGain();
  const bed = context.createGain();
  const compressor = context.createDynamicsCompressor();
  const master = context.createGain();
  fx.gain.value = sound;
  bed.gain.value = music;
  compressor.threshold.value = -9;
  compressor.knee.value = 10;
  compressor.ratio.value = 4;
  compressor.attack.value = 0.008;
  compressor.release.value = 0.25;
  master.gain.value = 0.74;
  fx.connect(compressor); bed.connect(compressor); compressor.connect(master); master.connect(context.destination);
  return { sound: fx, music: bed, dispose: () => { fx.disconnect(); bed.disconnect(); compressor.disconnect(); master.disconnect(); } };
}

export function scheduleNote(context: BaseAudioContext, destination: AudioNode, note: Note, start: number, ended: () => void = () => {}): Voice {
  const envelope = context.createGain();
  const filter = context.createBiquadFilter();
  const pan = context.createStereoPanner();
  const at = Math.max(context.currentTime, start);
  const duration = note.duration;
  const attack = Math.min(note.attack, duration * 0.4);
  const release = Math.min(note.release, duration - attack - 0.005);
  const source = note.wave === "noise" ? context.createBufferSource() : context.createOscillator();
  if (source instanceof AudioBufferSourceNode) { source.buffer = noise(context); source.loop = true; }
  else {
    source.type = note.wave as OscillatorType;
    source.frequency.setValueAtTime(note.frequency, at);
    if (note.endFrequency) source.frequency.exponentialRampToValueAtTime(note.endFrequency, at + duration);
  }
  filter.type = "lowpass";
  filter.frequency.value = note.cutoff;
  filter.Q.value = 0.45;
  pan.pan.value = note.pan;
  envelope.gain.setValueAtTime(0, at);
  envelope.gain.linearRampToValueAtTime(note.gain, at + attack);
  envelope.gain.setValueAtTime(note.gain, at + duration - release);
  envelope.gain.exponentialRampToValueAtTime(0.00001, at + duration - 0.004);
  envelope.gain.linearRampToValueAtTime(0, at + duration);
  source.connect(filter); filter.connect(envelope); envelope.connect(pan); pan.connect(destination);
  let disconnected = false;
  let stopped = false;
  const disconnect = () => {
    if (disconnected) return;
    disconnected = true;
    source.disconnect(); filter.disconnect(); envelope.disconnect(); pan.disconnect();
    ended();
  };
  source.onended = disconnect;
  source.start(at);
  source.stop(at + duration + 0.01);
  return {
    gain: note.gain,
    disconnect,
    stop: () => {
      if (stopped || disconnected) return;
      stopped = true;
      const now = context.currentTime;
      envelope.gain.cancelAndHoldAtTime(now);
      envelope.gain.linearRampToValueAtTime(0, now + 0.025);
      source.stop(now + 0.03);
    },
  };
}

/** Uses the real browser synthesizer for previews and OfflineAudioContext verification. */
export function renderScore(context: BaseAudioContext, score: Score, destination: AudioNode, start = 0): void {
  for (const note of score.notes) scheduleNote(context, destination, note, start + note.at);
}

/** A complete music loop runs on the audio clock even when browser timers are throttled. */
export async function renderMusicBuffer(score: Score): Promise<AudioBuffer> {
  const sampleRate = 24000;
  const context = new OfflineAudioContext(2, Math.ceil(score.duration * sampleRate), sampleRate);
  renderScore(context, score, context.destination);
  return context.startRendering();
}

export function pcmWav(buffer: AudioBuffer): Uint8Array {
  const channels = buffer.numberOfChannels;
  const bytes = new Uint8Array(44 + buffer.length * channels * 2);
  const view = new DataView(bytes.buffer);
  const text = (offset: number, value: string) => { for (let i = 0; i < value.length; i++) view.setUint8(offset + i, value.charCodeAt(i)); };
  text(0, "RIFF"); view.setUint32(4, bytes.length - 8, true); text(8, "WAVE"); text(12, "fmt ");
  view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, channels, true);
  view.setUint32(24, buffer.sampleRate, true); view.setUint32(28, buffer.sampleRate * channels * 2, true);
  view.setUint16(32, channels * 2, true); view.setUint16(34, 16, true); text(36, "data"); view.setUint32(40, bytes.length - 44, true);
  const data = Array.from({ length: channels }, (_, channel) => buffer.getChannelData(channel));
  for (let frame = 0; frame < buffer.length; frame++) for (let channel = 0; channel < channels; channel++) {
    const value = Math.max(-1, Math.min(1, data[channel]![frame]!));
    view.setInt16(44 + (frame * channels + channel) * 2, Math.round(value * (value < 0 ? 32768 : 32767)), true);
  }
  return bytes;
}
